import {Link, useLocation, useNavigate} from 'react-router-dom';
import {
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import clsx from 'clsx';
import {useAuth} from '@common/auth/use-auth';
import {useLogout} from '@common/auth/requests/logout';
import {removeFromLocalStorage} from '@common/utils/hooks/local-storage';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {NavbarAuthMenuNew} from '@common/ui/navigation/navbar/navbar-auth-menu-new';
import {NotificationDialogTrigger} from '@common/notifications/dialog/notification-dialog-trigger';
import {
  resolveSiteLogoSrc,
  SITE_LOGO_ALT,
  SITE_LOGO_NAVBAR_CLASS,
} from '@common/branding/site-logo';
import {useSettings} from '@common/core/settings/use-settings';
import {useThemeSelector} from '@common/ui/themes/theme-selector-context';
import {useSlidingPill} from '@common/ui/navigation/use-sliding-pill';
import {
  NAVBAR_DESIGN_HEIGHT,
  NAVBAR_DESIGN_WIDTH,
  useNavbarViewportScale,
} from '@common/ui/navigation/use-navbar-viewport-scale';

export type NavbarKey = 'send' | 'drive' | 'features' | 'plans';

const DESKTOP_NAV_KEYS: NavbarKey[] = ['send', 'drive', 'features', 'plans'];

const featureDropdownItems = [
  {key: 'storage', label: 'Storage', href: '/storage'},
  {key: 'share', label: 'Share', href: '/share'},
  {key: 'privacy', label: 'Privacy', href: '/privacy'},
  {key: 'security', label: 'Security', href: '/security'},
] as const;

type FeatureKey = (typeof featureDropdownItems)[number]['key'];

const FEATURE_KEYS: FeatureKey[] = [
  'storage',
  'share',
  'privacy',
  'security',
];

function activeFeatureFromPath(pathname: string): FeatureKey | null {
  const match = featureDropdownItems.find(
    item => pathname === item.href || pathname.startsWith(`${item.href}/`),
  );
  return match?.key ?? null;
}

function featureDropdownLinkClass(isHighlighted: boolean) {
  return clsx(
    'relative z-[1] block rounded-[16px] px-[14px] py-[12px] text-center text-[14px] font-medium leading-none transition-colors duration-200',
    isHighlighted
      ? 'text-[#3C7D44]'
      : 'text-muted',
  );
}

function FeaturesDropdownMenu({coordinateScale = 1}: {coordinateScale?: number}) {
  const {pathname} = useLocation();
  const activeFeature = activeFeatureFromPath(pathname);
  const {
    trackRef,
    setItemRef,
    setHoveredKey,
    pillStyle,
    pillTransitionClass,
    isHighlighted,
  } = useSlidingPill(FEATURE_KEYS, activeFeature, {coordinateScale});

  return (
    <div
      ref={trackRef}
      onMouseLeave={() => setHoveredKey(null)}
      className="relative rounded-[18px] border border-divider bg-alt p-[3px] shadow-[0_12px_28px_rgba(15,23,42,0.12)]"
    >
      <div
        aria-hidden
        className={clsx(
          'pointer-events-none absolute z-0 rounded-[16px]',
          'border-[2px] border-solid bg-alt dark:bg-[#232832]',
          pillTransitionClass,
          pillStyle ? 'opacity-100' : 'opacity-0',
        )}
        style={{
          borderColor: NAV_HIGHLIGHT_BORDER,
          left: pillStyle?.left ?? 0,
          top: pillStyle?.top ?? 0,
          width: pillStyle?.width ?? 0,
          height: pillStyle?.height ?? 0,
        }}
      />
      {featureDropdownItems.map(item => (
        <div
          key={item.key}
          ref={setItemRef(item.key)}
          onMouseEnter={() => setHoveredKey(item.key)}
        >
          <Link
            to={item.href}
            className={featureDropdownLinkClass(isHighlighted(item.key))}
          >
            {item.label}
          </Link>
        </div>
      ))}
    </div>
  );
}

interface Props {
  active?: NavbarKey;
  className?: string;
  viewportScale?: number;
}

const PROFILE_AVATAR_BG = '#D7EEFF';
const PROFILE_AVATAR_TEXT = '#3EA4F2';
const PROFILE_AVATAR_CLASS =
  'flex shrink-0 items-center justify-center overflow-hidden rounded-[24px]';

const NAVBAR_BAR_BG_CLASS = 'bg-paper dark:bg-[#2f333b]';

/** Figma nav / plans tab active stroke */
const NAV_HIGHLIGHT_BORDER = '#3C7D44';

const NAVBAR_TRACK_BORDER_CLASS =
  'border-2 border-solid border-divider dark:border-[#2a2d33]';

const NAVBAR_NOTIFICATION_BUTTON_CLASS = clsx(
  'flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-[24px]',
  'border-2 border-divider text-muted',
  NAVBAR_BAR_BG_CLASS,
);

interface NavbarLoginSignupLinksProps {
  className?: string;
  onNavigate?: () => void;
}

/** Figma "Header login button" — 138×60; Login text left-aligned (pl 17px), not centered */
const NAVBAR_LOGIN_LINK_CLASS = clsx(
  'box-border flex h-[60px] w-[138px] shrink-0 items-center justify-start pl-[17px]',
  'rounded-[24px] border-2 border-solid border-[#4C4C4C] bg-white',
  'dark:border-[#3EA4F2] dark:bg-white dark:text-[#64B466]',
  'font-[Inter,sans-serif] text-[16px] font-medium leading-normal text-[#64B466]',
);

/** Figma "Header signup button" — 100×60, 2px #4C4C4C, fill #4C4C4C, Signup text #FFF 16/500 */
const NAVBAR_SIGNUP_LINK_CLASS = clsx(
  'box-border -ml-[60px] z-10 flex h-[60px] w-[100px] shrink-0 items-center justify-center',
  'rounded-[24px] rounded-r-[22px] border-2 border-solid border-[#4C4C4C] bg-[#4C4C4C]',
  'dark:border-[#5a5a5a] dark:bg-[#5a5a5a]',
  'font-[Inter,sans-serif] text-[16px] font-medium leading-normal text-white'
);

/**
 * Figma auth group: two pills (138 + 100 − 60 overlap = 178px wide), no outer wrapper.
 */
export function NavbarLoginSignupLinks({
  className,
  onNavigate,
}: NavbarLoginSignupLinksProps) {
  return (
    <div className={clsx('flex shrink-0 items-center', className)}>
      <Link
        to="/login"
        onClick={onNavigate}
        className={NAVBAR_LOGIN_LINK_CLASS}
      >
        Login
      </Link>
      <Link
        to="/register"
        onClick={onNavigate}
        className={NAVBAR_SIGNUP_LINK_CLASS}
      >
        Signup
      </Link>
    </div>
  );
}

export function getUserInitials(user?: {
  first_name?: string;
  last_name?: string;
  display_name?: string;
  email?: string;
}) {
  const firstName = user?.first_name?.trim() ?? '';
  const lastName = user?.last_name?.trim() ?? '';

  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }

  if (firstName) {
    return firstName.slice(0, 2).toUpperCase();
  }

  const name = (user?.display_name || user?.email || '').trim();
  const parts = name.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  return (first + second || name.slice(0, 2) || 'U').toUpperCase();
}

export function hasUploadedAvatar(avatar?: string | null) {
  if (!avatar) return false;
  const isGravatarRetro =
    avatar.includes('gravatar.com/avatar') &&
    (avatar.includes('d=retro') || avatar.includes('d=identicon'));
  return !isGravatarRetro;
}

interface NavbarDesktopNavItemProps {
  widthClass: string;
  className?: string;
  children: ReactNode;
  registerRef: (node: HTMLDivElement | null) => void;
  onMouseEnter: () => void;
}

function NavbarDesktopNavItem({
  widthClass,
  className,
  children,
  registerRef,
  onMouseEnter,
}: NavbarDesktopNavItemProps) {
  return (
    <div
      ref={registerRef}
      onMouseEnter={onMouseEnter}
      className={clsx(
        'relative z-[1] shrink-0 overflow-visible',
        widthClass,
        className,
      )}
    >
      {children}
    </div>
  );
}

function navbarDesktopLinkClass(isHighlighted: boolean) {
  return clsx(
    'flex h-[60px] w-full items-center justify-center',
    'border-0 bg-transparent p-0 text-[16px] font-medium leading-normal transition-colors duration-200',
    isHighlighted ? 'text-[#3C7D44]' : 'text-main',
  );
}

export function SendStyleNavbar({active, className, viewportScale}: Props) {
  const fallbackScale = useNavbarViewportScale();
  const scale = viewportScale ?? fallbackScale.scale;
  const shouldScaleDesktopNav = scale > 0 && scale < 1;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileFeaturesOpen, setIsMobileFeaturesOpen] = useState(
    active === 'features'
  );
  const [isFeaturesMenuOpen, setIsFeaturesMenuOpen] = useState(false);
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const {user, isLoggedIn} = useAuth();
  const logout = useLogout();
  const {auth} = useContext(SiteConfigContext);
  const {branding} = useSettings();
  const {selectedTheme} = useThemeSelector();
  const logoSrc = resolveSiteLogoSrc(branding, !!selectedTheme?.is_dark);
  const {
    trackRef,
    setItemRef,
    setHoveredKey,
    pillStyle,
    pillTransitionClass,
    isHighlighted,
  } = useSlidingPill(DESKTOP_NAV_KEYS, active, {
    coordinateScale: shouldScaleDesktopNav ? scale : 1,
  });

  const goToHomepage = () => {
    setIsMobileMenuOpen(false);
    removeFromLocalStorage('be.onboarding.selected');
    navigate('/');
  };

  const userName = user?.display_name || user?.email || 'User';
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => setAvatarFailed(false), [user?.avatar]);
  const showImageAvatar = hasUploadedAvatar(user?.avatar) && !avatarFailed;
  const userInitials = getUserInitials(user ?? undefined);

  const mobileLinkClass = (key: NavbarKey) =>
    clsx(
      'rounded-[12px] border-2 px-[10px] py-[10px] text-center text-main',
      active === key ? 'border-primary text-primary' : 'border-divider'
    );

  const accountLink =
    isLoggedIn && user && auth.getUserProfileLink
      ? auth.getUserProfileLink(user)
      : '/account-settings';

  const avatar = showImageAvatar ? (
    <img
      src={user!.avatar}
      alt={userName}
      onError={() => setAvatarFailed(true)}
      className="h-full w-full object-cover"
    />
  ) : (
    <span
      className="text-[17px] font-medium leading-none"
      style={{color: PROFILE_AVATAR_TEXT}}
    >
      {userInitials}
    </span>
  );

  const renderDesktopNavRow = () => (
    <>
      <div
        className={clsx(
          'relative box-border flex h-[60px] min-h-0 min-w-0 flex-1 items-center justify-between overflow-visible rounded-[24px] pl-[18px]',
          NAVBAR_BAR_BG_CLASS,
          isLoggedIn ? 'pr-[18px]' : 'pr-0',
        )}
      >
        <div
          aria-hidden
          className={clsx(
            'pointer-events-none absolute inset-0 z-0 rounded-[24px]',
            NAVBAR_TRACK_BORDER_CLASS,
          )}
        />
        <Link
          to="/"
          onClick={e => {
            e.preventDefault();
            goToHomepage();
          }}
          className="relative z-[1] shrink-0"
        >
          <img
            src={logoSrc}
            alt={SITE_LOGO_ALT}
            className={SITE_LOGO_NAVBAR_CLASS}
          />
        </Link>

        <div
          ref={trackRef}
          className="relative z-30 mx-auto flex min-h-0 min-w-0 flex-1 items-center justify-center gap-[17px] overflow-visible"
          onMouseLeave={() => setHoveredKey(null)}
        >
          <div
            aria-hidden
            className={clsx(
              'pointer-events-none absolute z-0 rounded-[24px]',
              'border-[2px] border-solid bg-paper dark:bg-[#2f333b]',
              pillTransitionClass,
              pillStyle ? 'opacity-100' : 'opacity-0',
            )}
            style={{
              borderColor: NAV_HIGHLIGHT_BORDER,
              left: pillStyle?.left ?? 0,
              top: pillStyle?.top ?? 0,
              width: pillStyle?.width ?? 0,
              height: pillStyle?.height ?? 0,
            }}
          />
          <NavbarDesktopNavItem
            widthClass="w-[105px]"
            registerRef={setItemRef('send')}
            onMouseEnter={() => setHoveredKey('send')}
          >
            <Link
              to="/send"
              className={`${navbarDesktopLinkClass(isHighlighted('send'))} font-['Inter',sans-serif]`}
          >
              Send
          </Link>
          </NavbarDesktopNavItem>
          <NavbarDesktopNavItem
            widthClass="w-[112px]"
            registerRef={setItemRef('drive')}
            onMouseEnter={() => setHoveredKey('drive')}
          >
            <Link
              to="/drive"
              className={`${navbarDesktopLinkClass(isHighlighted('drive'))} font-['Inter',sans-serif]`}
          >
               My Drive
          </Link>
          </NavbarDesktopNavItem>
          <NavbarDesktopNavItem
            widthClass="w-[118px]"
            className="z-40"
            registerRef={setItemRef('features')}
            onMouseEnter={() => setHoveredKey('features')}
          >
            <div
              className="relative z-50"
              onMouseEnter={() => setIsFeaturesMenuOpen(true)}
              onMouseLeave={() => setIsFeaturesMenuOpen(false)}
            >
              <button
                type="button"
                className={clsx(
                    navbarDesktopLinkClass(isHighlighted('features')),
                    "m-0 w-full appearance-none font-['Inter',sans-serif]",
                )}
                aria-haspopup="menu"
                aria-expanded={isFeaturesMenuOpen || active === 'features'}
            >
                Features
            </button>
              <div
                className={clsx(
                  'absolute left-1/2 top-full z-[200] w-[132px] -translate-x-1/2 pt-[8px] transition-all duration-150',
                  isFeaturesMenuOpen
                    ? 'pointer-events-auto visible opacity-100'
                    : 'pointer-events-none invisible opacity-0',
                )}
              >
                <FeaturesDropdownMenu
                  coordinateScale={shouldScaleDesktopNav ? scale : 1}
                />
              </div>
            </div>
          </NavbarDesktopNavItem>
          <NavbarDesktopNavItem
            widthClass="w-[105px]"
            registerRef={setItemRef('plans')}
            onMouseEnter={() => setHoveredKey('plans')}
          >
            <Link
                to="/pricing"
                className={clsx(
                    navbarDesktopLinkClass(isHighlighted('plans')),
                    "font-['Inter',sans-serif]",
                )}
            >
                Plans
            </Link>
          </NavbarDesktopNavItem>
        </div>

        {!isLoggedIn ? (
          <NavbarLoginSignupLinks className="relative z-[1] me-0 shrink-0 self-stretch overflow-visible rounded-r-[22px]" />
        ) : null}
      </div>

      {isLoggedIn ? (
        <div className="flex shrink-0 items-center gap-[12px]">
          <NotificationDialogTrigger
            size={null}
            radius="rounded-[24px]"
            disableHover
            viewportScale={shouldScaleDesktopNav ? scale : 1}
            className={NAVBAR_NOTIFICATION_BUTTON_CLASS}
          />
          <NavbarProfileAvatarButton />
        </div>
      ) : null}
    </>
  );

  return (
    <div className={clsx(className, 'min-w-0')}>
      <div
        className={clsx(
          'rounded-[20px] border-2 border-divider px-[14px] py-[10px] lg:hidden',
          NAVBAR_BAR_BG_CLASS,
        )}
      >
        <div className="flex items-center justify-between">
          <Link
            to="/"
            onClick={e => {
              e.preventDefault();
              goToHomepage();
            }}
          >
            <img
              src={logoSrc}
              alt={SITE_LOGO_ALT}
              className={SITE_LOGO_NAVBAR_CLASS}
            />
          </Link>
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(prev => !prev)}
            className={clsx(
              'flex h-[40px] w-[44px] items-center justify-center rounded-[12px] border-2 border-primary',
              NAVBAR_BAR_BG_CLASS,
            )}
            aria-label="Toggle navigation"
          >
            <span className="relative block h-[16px] w-[18px]">
              <span className="absolute left-0 top-0 h-[2px] w-full bg-primary" />
              <span className="absolute left-0 top-[7px] h-[2px] w-full bg-primary" />
              <span className="absolute left-0 top-[14px] h-[2px] w-full bg-primary" />
            </span>
          </button>
        </div>
      </div>

      <div
        className={clsx(
          'fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 lg:hidden',
          isMobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={() => setIsMobileMenuOpen(false)}
      />
      <aside
        className={clsx(
          'fixed right-0 top-0 z-50 h-full w-[280px] bg-paper p-[14px] shadow-[-8px_0_24px_rgba(15,23,42,0.18)] transition-transform duration-300 lg:hidden',
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="mb-[14px] flex items-center justify-between">
          <p className="text-[16px] font-semibold text-main">Menu</p>
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex h-[32px] w-[32px] items-center justify-center rounded-[10px] border border-divider text-main"
            aria-label="Close menu"
          >
            x
          </button>
        </div>
        <div className="grid grid-cols-1 gap-[8px] text-[14px] font-medium text-main">
          <Link
            to="/send"
            onClick={() => setIsMobileMenuOpen(false)}
            className={mobileLinkClass('send')}
          >
            Send
          </Link>
          <Link
            to="/drive"
            onClick={() => setIsMobileMenuOpen(false)}
            className={mobileLinkClass('drive')}
          >
            My Drive
          </Link>
          <div className="rounded-[12px] border-2 border-divider px-[6px] py-[6px]">
            <button
              type="button"
              onClick={() => setIsMobileFeaturesOpen(prev => !prev)}
              className={clsx(
                'flex w-full items-center justify-between rounded-[10px] px-[10px] py-[10px] text-left',
                active === 'features' ? 'text-primary' : 'text-main'
              )}
              aria-expanded={isMobileFeaturesOpen}
              aria-controls="mobile-features-submenu"
            >
              <span className='ml-[80px]'>Features</span>
              <span
                className={clsx(
                  'inline-flex h-[18px] w-[18px] items-center justify-center transition-transform duration-200 ',
                  isMobileFeaturesOpen ? 'rotate-180' : ''
                )}
              >
                <svg
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                  className="h-[14px] w-[14px]"
                >
                  <path
                    d="M5.2 7.4L10 12.2l4.8-4.8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            {isMobileFeaturesOpen ? (
              <div
                id="mobile-features-submenu"
                className="mt-[4px] grid grid-cols-1 gap-[6px] pb-[2px]"
              >
                {featureDropdownItems.map(item => (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={clsx(
                      'rounded-[10px] border px-[10px] py-[8px] text-[13px] transition-colors',
                      activeFeatureFromPath(pathname) === item.key
                        ? 'border-[#3C7D44] text-[#3C7D44]'
                        : 'border-transparent text-muted hover:border-primary hover:text-primary',
                    )}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
          <Link
            to="/pricing"
            onClick={() => setIsMobileMenuOpen(false)}
            className={mobileLinkClass('plans')}
          >
            Plans
          </Link>
          {!isLoggedIn ? (
            <NavbarLoginSignupLinks
              className="justify-center"
              onNavigate={() => setIsMobileMenuOpen(false)}
            />
          ) : (
            <>
              <div className="mb-[2px] mt-[2px] flex items-center gap-[10px] rounded-[12px] border border-divider px-[10px] py-[8px] pl-[80px]">
                <div
                  className={clsx(
                    PROFILE_AVATAR_CLASS,
                    'h-[40px] w-[40px] rounded-[16px]',
                    showImageAvatar ? 'bg-paper' : 'bg-[#D7EEFF]'
                  )}
                >
                  {avatar}
                </div>
                <span className="truncate text-[13px] text-muted">
                  {userName}
                </span>
              </div>
              <Link
                to={accountLink}
                onClick={() => setIsMobileMenuOpen(false)}
                className="rounded-[12px] border border-primary px-[10px] py-[10px] text-center text-main"
              >
                Account
              </Link>
              <Link
                to="/account-settings"
                onClick={() => setIsMobileMenuOpen(false)}
                className="rounded-[12px] border border-transparent px-[10px] py-[10px] text-center text-muted"
              >
                Settings
              </Link>
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  logout.mutate();
                }}
                className="rounded-[12px] border border-transparent px-[10px] py-[10px] text-center text-danger"
              >
                Logout
              </button>
            </>
          )}
        </div>
      </aside>

      {shouldScaleDesktopNav ? (
        <div
          className="relative mx-auto hidden max-w-full overflow-visible lg:block"
          style={{
            width: NAVBAR_DESIGN_WIDTH * scale,
            minHeight: NAVBAR_DESIGN_HEIGHT * scale,
          }}
        >
          <div
            className="absolute left-0 top-0 flex items-center gap-[12px] overflow-visible"
            style={{
              width: NAVBAR_DESIGN_WIDTH,
              minHeight: NAVBAR_DESIGN_HEIGHT,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          >
            {renderDesktopNavRow()}
          </div>
        </div>
      ) : (
        <div className="hidden w-full items-center gap-[12px] overflow-visible lg:flex">
          {renderDesktopNavRow()}
        </div>
      )}
    </div>
  );
}

interface NavbarProfileAvatarButtonProps {
  size?: 40 | 60;
  className?: string;
}

/** Figma profile control: 60×60, #D7EEFF, 24px radius (logged-in only). */
export function NavbarProfileAvatarButton({
  size = 60,
  className,
}: NavbarProfileAvatarButtonProps) {
  const {user, isLoggedIn} = useAuth();
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => setAvatarFailed(false), [user?.avatar]);

  if (!isLoggedIn || !user) {
    return null;
  }

  const userName = user.display_name || user.email || 'User';
  const showImageAvatar = hasUploadedAvatar(user.avatar) && !avatarFailed;
  const userInitials = getUserInitials(user);

  const avatar = showImageAvatar ? (
    <img
      src={user.avatar}
      alt={userName}
      onError={() => setAvatarFailed(true)}
      className="h-full w-full object-cover"
    />
  ) : (
    <span
      className="text-[17px] font-medium leading-none"
      style={{color: PROFILE_AVATAR_TEXT}}
    >
      {userInitials}
    </span>
  );

  return (
    <NavbarAuthMenuNew>
      <button
        type="button"
        className={clsx(
          PROFILE_AVATAR_CLASS,
          size === 60
            ? 'h-[60px] w-[60px]'
            : 'h-[40px] w-[40px] rounded-[16px]',
          showImageAvatar
            ? 'border-2 border-divider bg-paper'
            : 'border-0 bg-[#D7EEFF]',
          className
        )}
        aria-label="Account menu"
      >
        {avatar}
      </button>
    </NavbarAuthMenuNew>
  );
}
